<template>
  <div
    :class="[swatchClassName, compactMode ? 'side-navigation-menu-compact' : 'side-navigation-menu']"
    @click="forwardClick"
  >
    <slot />
    <div class="menu-container" v-if="compactMode === false">
      <div>
        <div class="buttons-column" v-for="item in navigationItems" :key="item.titleKey">
          <div class="column-header">{{ $t(item.titleKey).toUpperCase() }}</div>
          <DxButton v-for="link in item.items" :key="link.titleKey" :visible="navigationItemVisible(link)"
              :icon="link.icon"
              type="default"
              :text="localization.t(link.titleKey)"
              styling-mode="text"
              @click="navigate(link)"
            />
        </div>
      </div>
    </div>
    <div class="menu-container" v-if="compactMode === true">
      <div>
        <div class="buttons-column-compact" v-for="item in navigationItems" :key="item.titleKey">
          <div class="column-header-compact">{{ $t(item.titleKey).toUpperCase() }}</div>
          <DxButton v-for="link in item.items" :key="link.titleKey" :visible="navigationItemVisible(link)"
              :icon="link.icon"
              type="default"
              styling-mode="text"
            />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { sizes } from '../utils/media-query';
import navigation from '../app-navigation';
import { ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { themeService } from '../theme-service';
import { DxButton, DxList } from 'devextreme-vue';

import { useLocalization } from '@/locales/localization';
const localization = useLocalization();

import { useAuthStore } from '@/stores/auth';
const auth = useAuthStore();

const openState = ref(true);

const props = defineProps({
  compactMode: { type: Boolean },
});
const emit = defineEmits(["click"]);

const isLargeScreen = sizes()['screen-large'];
const swatchClassName = ref('');

const route = useRoute();
const router = useRouter();

const navigationItems = navigation.filter(o => navigationGroupVisible(o.items)).map((item) => {
  if(item.path && !(/^\//.test(item.path))){
    item.path = `/${item.path}`;
  }
  return {...item, expanded: isLargeScreen };
});

function navigationGroupVisible(items) {
  var result = false;
  for (var i = 0; i < items.length; i++) {
    if (navigationItemVisible(items[i])) {
      result = true;
      break;
    }
  }

  return result;
}

function navigationItemVisible(item) {
  if (!item.requiredPermission) return true;
  return auth.hasPermission(item.requiredPermission);
}

function navigate(item) {
  router.push(item.path);
}

function forwardClick (...args) {
  emit("click", args);
}

function handleItemClick(e) {
  if (!e.itemData.path || props.compactMode) {
    return;
  }
  router.push(e.itemData.path);

  const pointerEvent = e.event;
  pointerEvent.stopPropagation();
}

watch(
  () => themeService.isDark,
  () => {
    swatchClassName.value = themeService.isDark.value ? 'dx-swatch-additional-dark' : 'dx-swatch-additional';
  },
  { immediate: true }
);
</script>

<style lang="scss">
@import "../variables.scss";
@import "../dx-styles.scss";

/* Buttons container */
.dx-swatch-additional, .dx-swatch-additional-dark {
  &.side-navigation-menu {
    display: flex;
    flex-direction: column;
    min-height: 100%;
    height: 100%;
    width: 250px !important;
    background-color: var(--base-bg);

    .menu-container {
      min-height: 100%;
      display: flex;
      flex: 1;
    }
  }
}

.buttons-column > .column-header {
  flex-grow: 0;
  width: 150px;
  font-size: 100%;
  opacity: 0.4;
  text-align: left;
  padding: 8px;
  padding-top: 1em;
  font-weight: bold;
}

.buttons-column > div {
  width: 100%;
  text-align: start;
}

.buttons-column {
  width: 100%;
  justify-content: start;
}

.buttons-column .dx-button-content {
  justify-content: start;
}

/* Buttons container - compact */
.dx-swatch-additional, .dx-swatch-additional-dark {
  &.side-navigation-menu-compact {
    display: flex;
    flex-direction: column;
    min-height: 100%;
    height: 100%;
    width: 60px !important;
    background-color: var(--base-bg);

    .menu-container {
      min-height: 100%;
      display: flex;
      justify-content: center;
    }
  }
}

.buttons-column-compact > .column-header-compact {
  width: 40px;
  padding: 8px;
  padding-top: 1em;
  opacity: 0;
}

.buttons-column-compact {
  padding-left: 3px;
}

/* DX overrides */
.dx-swatch-additional .dx-button-mode-text.dx-button-default.dx-state-focused,
.dx-swatch-additional .dx-button-mode-text.dx-button-default.dx-state-hover,
.dx-swatch-additional .dx-button-mode-text.dx-button-default.dx-state-hover .dx-icon {
  background-color: var(--dx-color-primary);
  color: #fff;
}

.dx-swatch-additional-dark .dx-button-mode-text.dx-button-default {
  color: #fff;
}

.dx-swatch-additional-dark .dx-button-mode-text.dx-button-default .dx-icon {
  color: #fff;
  opacity: 0.2;
}

.dx-swatch-additional-dark .dx-button-mode-text.dx-button-default.dx-state-hover .dx-icon {
  opacity: 1;
}
</style>
